// A pad to draw a signature on with a mouse, a pen or a finger (ported from
// pdf-web-app's SignatureDrawingDialog): pressure-like strokes, thicker where
// the hand slows, in the given ink, on a transparent canvas. SignTools' Draw
// tab, and a page of its own to sign on a phone.
//
//   <DrawPad color="#000000" onDraft={(canvas) => …} texts={{ drawHere, clear }} ratio={2} />
//
// onDraft(canvas) after every stroke (null once cleared) — signature.js
// signaturePng(canvas) makes the PNG of it. `ratio` is the pad's width to its
// height; `showClear` false when the host has a Clear of its own (a new `key`
// makes the pad anew, empty).
import React, { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

const CANVAS_W = 1200

/** Slower movement, a thicker line; the width eased so it never jumps. */
function strokeWidth(speed, previous) {
  const min = 4
  const max = 12
  const target = max - Math.min(speed / 1.2, 1) * (max - min)
  return previous ? previous * 0.8 + target * 0.2 : target
}

function drawStroke(ctx, stroke, color) {
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  // a tap: a dot
  if (stroke.length === 1) {
    ctx.beginPath()
    ctx.arc(stroke[0].x, stroke[0].y, stroke[0].width / 2, 0, Math.PI * 2)
    ctx.fill()
    return
  }
  // quadratic curves through the midpoints, each segment at its own width
  for (let i = 0; i < stroke.length - 1; i++) {
    const point = stroke[i]
    const next = stroke[i + 1]
    ctx.beginPath()
    ctx.lineWidth = point.width
    if (i === 0) {
      ctx.moveTo(point.x, point.y)
      ctx.lineTo(next.x, next.y)
    } else {
      const prev = stroke[i - 1]
      ctx.moveTo((prev.x + point.x) / 2, (prev.y + point.y) / 2)
      ctx.quadraticCurveTo(point.x, point.y, (point.x + next.x) / 2, (point.y + next.y) / 2)
    }
    ctx.stroke()
  }
}

export default function DrawPad({ color, onDraft, texts, ratio = 2, showClear = true, className = "" }) {
  const canvasRef = useRef(null)
  const strokes = useRef([])
  const current = useRef(null)
  const last = useRef(null)
  const [empty, setEmpty] = useState(true)

  const redraw = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    for (const stroke of strokes.current) drawStroke(ctx, stroke, color)
    if (current.current) drawStroke(ctx, current.current, color)
  }

  // a new ink redraws what is there
  useEffect(() => {
    redraw()
    if (strokes.current.length) onDraft(canvasRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color])

  const pointOf = (event) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    return { x: ((event.clientX - rect.left) * canvas.width) / rect.width, y: ((event.clientY - rect.top) * canvas.height) / rect.height }
  }

  const start = (event) => {
    event.preventDefault()
    canvasRef.current.setPointerCapture?.(event.pointerId)
    const point = pointOf(event)
    current.current = [{ ...point, width: 6 }]
    last.current = { point, time: performance.now() }
    redraw()
  }

  const move = (event) => {
    if (!current.current) return
    event.preventDefault()
    const point = pointOf(event)
    const now = performance.now()
    const { point: from, time } = last.current
    const dx = point.x - from.x
    const dy = point.y - from.y
    const distance = Math.hypot(dx, dy)
    if (distance < 1) return
    const stroke = current.current
    const prevWidth = stroke[stroke.length - 1].width
    const width = strokeWidth(now > time ? distance / (now - time) : 0, prevWidth)
    // a point every ~3 pixels, for smooth curves
    const segments = Math.max(2, Math.floor(distance / 3))
    for (let i = 1; i <= segments; i++) {
      const t = i / segments
      stroke.push({ x: from.x + dx * t, y: from.y + dy * t, width: prevWidth + (width - prevWidth) * t })
    }
    last.current = { point, time: now }
    redraw()
  }

  const end = () => {
    if (!current.current) return
    strokes.current.push(current.current)
    current.current = null
    last.current = null
    redraw()
    setEmpty(false)
    onDraft(canvasRef.current)
  }

  const clear = () => {
    strokes.current = []
    current.current = null
    redraw()
    setEmpty(true)
    onDraft(null)
  }

  return (
    <div className={twMerge("relative w-full rounded-md border border-dashed border-admin_bordered bg-white overflow-hidden", className)} style={{ aspectRatio: `${ratio} / 1` }} data-testid="sign-draw">
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={Math.round(CANVAS_W / ratio)}
        className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      {empty && <span className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-gray-400 select-none">{texts.drawHere}</span>}
      {/* the line a signature sits on */}
      <span className="pointer-events-none absolute left-[8%] right-[8%] bottom-[22%] border-b border-gray-300" />
      {showClear && !empty && (
        <button type="button" onClick={clear} className="absolute top-2 right-2 text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">
          {texts.clear}
        </button>
      )}
    </div>
  )
}
